import React from 'react';

import cs from 'classnames';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import ReactDOMServer from 'react-dom/server';
import ReactHtmlParser, {convertNodeToElement} from 'react-html-parser';

import {StyledUserContentDiv} from 'app/procedures/styles';
import {useAccountSettingsState} from 'app/settings/api';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import {
  nodeProcessors,
  type NodeType,
  type ProcessorExtraOptions,
} from './nodeProcessors';
import {
  attachHtmldiffIndex,
  groupDocumentImages,
  reformatHTMLParserNodes,
} from './utils/structure';
import {
  highlightTokenHtmlNode,
  splitNodeForTokenHighlights,
} from './utils/tokens';

import type {
  HtmlDocType,
  TextNodeTypeWithParent,
} from './nodeProcessors/node-processors-types';
import type {TextHighlightType} from 'app/comments/types';

interface Props {
  id?: string;
  content: string | React.ReactElement<'div'> | React.ReactElement<'span'>;
  imageSize?: 'large' | 'small' | 'original';
  visibleTextHighlights?: Array<TextHighlightType>;
  visibleTextHighlightsKeys?: string;
  innerRef?:
    | React.MutableRefObject<HTMLDivElement | null>
    | ((ref: HTMLDivElement) => void);
}

export const UserContentContainer = React.memo(
  ({
    id,
    content,
    imageSize = 'large',
    innerRef,
    visibleTextHighlights,
    visibleTextHighlightsKeys,
    ...other
  }: Props) => {
    const {MEDIA_URL, MEDIA_URL_READ} = useAccountSettingsState().data!;

    const isDiffHighlightDisabled = typeof content === 'string';
    const isTextHighlightNeeded = visibleTextHighlightsKeys != null;

    function preprocessNodes(nodes: Array<NodeType>) {
      let nodesToReturn = nodes;
      if (isTextHighlightNeeded) {
        nodesToReturn = reformatHTMLParserNodes(nodes);
        attachHtmldiffIndex(nodesToReturn, isDiffHighlightDisabled ? 0 : -1);
      }
      nodesToReturn = groupDocumentImages(nodesToReturn);
      return nodesToReturn;
    }
    function transform(
      node: TextNodeTypeWithParent | HtmlDocType,
      index: number
    ): React.ReactElement<any, string | React.JSXElementConstructor<any>> {
      const opts: ProcessorExtraOptions = {
        mediaURL: MEDIA_URL,
        mediaURLRead: MEDIA_URL_READ,
        imageSize,
      };
      if (visibleTextHighlights?.length && shouldHighlight(node)) {
        node = applyHighlights(node as HtmlDocType, visibleTextHighlights);
      }

      const {node: processedNode, finalElement} = processWithCustomRenderers(
        // wrap videos, run embedly etc.
        node,
        index,
        transform,
        opts
      );

      if (finalElement) {
        // early exit if we reach the 'final' element.
        return finalElement;
      }

      return convertNodeToElement(processedNode, index, transform);
    }

    return (
      <StyledUserContentDiv
        id={id}
        ref={innerRef}
        className={cs('fr-view', {
          'right-to-left-text': isRightToLeftWrapper(content),
        })}
      >
        <div {...other}>
          {ReactHtmlParser(
            isDiffHighlightDisabled
              ? content
              : ReactDOMServer.renderToStaticMarkup(content),
            {
              transform,
              preprocessNodes,
            }
          )}
        </div>
      </StyledUserContentDiv>
    );
  },
  (prevProps: Props, props: Props) => {
    const clonedProps = omit(prevProps, 'innerRef', 'visibleTextHighlights');
    const clonedNextProps = omit(props, 'innerRef', 'visibleTextHighlights');

    return isEqual(clonedProps, clonedNextProps);
  }
);

function shouldHighlight(node) {
  return (
    node.type === 'tag' &&
    node.name !== 'del' &&
    node.attribs['data-diff-node'] !== 'del'
  );
}

function applyHighlights(
  node: HtmlDocType,
  highlights: Array<TextHighlightType>
) {
  if (node.children.length === 1 && node.children[0].type === 'text') {
    node = splitNodeForTokenHighlights(node, node.children[0], highlights);
  }
  for (const highlight of highlights) {
    // TODO: Potential bug, previous code checks for children.length === 1
    // does this code assume it ahs been split for token highlight or not care?
    node = highlightTokenHtmlNode(node, highlight);
  }
  return node;
}

function processWithCustomRenderers(
  node: HtmlDocType | TextNodeTypeWithParent,
  index: number,
  transform,
  opts: ProcessorExtraOptions
) {
  for (const processor of nodeProcessors) {
    if (node.type === 'text') {
      continue;
    }

    if (
      node.type === 'tag' &&
      processor.tag != null &&
      node.name !== processor.tag
    ) {
      continue;
    }

    if (!processor.condition(node)) {
      continue;
    }
    const [updatedNode, finalElement] = processor.processor(
      node,
      index,
      transform,
      opts
    );

    if (updatedNode != null) {
      node = updatedNode;
    }

    if (finalElement != null) {
      return {node, finalElement};
    }
  }

  return {node};
}
